import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';
import { doseAtVolumePercentGy, Dvh } from '~/domain/patient/dvh';

export interface NamedRoi {
    readonly roiNumber: number;
    readonly name: string;
}

export interface StructurePair {
    readonly name: string;
    readonly priorRoiNumber: number;
    readonly currentRoiNumber: number;
}

export interface StructurePairing {
    readonly pairs: readonly StructurePair[];
    readonly onlyPrior: readonly string[];
    readonly onlyCurrent: readonly string[];
}

// Pairs by trimmed name, in the prior set's order; the first match wins for a repeated name.
export function pairStructuresByName(
    prior: readonly NamedRoi[],
    current: readonly NamedRoi[]
): StructurePairing {
    const used = new Set<number>();
    const pairs: StructurePair[] = [];
    const onlyPrior: string[] = [];
    for (const roi of prior) {
        const match = current.find(
            (candidate) =>
                !used.has(candidate.roiNumber) &&
                DicomStructureSet.structureNamesTrimmedEqual(
                    candidate.name,
                    roi.name
                )
        );
        if (match) {
            used.add(match.roiNumber);
            pairs.push({
                name: roi.name,
                priorRoiNumber: roi.roiNumber,
                currentRoiNumber: match.roiNumber,
            });
        } else {
            onlyPrior.push(roi.name);
        }
    }
    const onlyCurrent = current
        .filter((roi) => !used.has(roi.roiNumber))
        .map((roi) => roi.name);
    return { pairs, onlyPrior, onlyCurrent };
}

export interface DvhSummary {
    readonly meanGy: number;
    readonly maxGy: number;
    readonly d95Gy?: number;
    readonly d2Gy?: number;
}

export function summarizeDvh(dvh: Dvh): DvhSummary {
    return {
        meanGy: dvh.doseMeanGy,
        maxGy: dvh.doseMaxGy,
        d95Gy: doseAtVolumePercentGy(dvh, 95),
        d2Gy: doseAtVolumePercentGy(dvh, 2),
    };
}

export type DvhMetric = keyof DvhSummary;

export const DVH_METRICS: readonly DvhMetric[] = [
    'meanGy',
    'maxGy',
    'd95Gy',
    'd2Gy',
];

// Current minus prior (Gy); undefined when either side has no value.
export function dvhMetricDifferenceGy(
    prior: DvhSummary,
    current: DvhSummary,
    metric: DvhMetric
): number | undefined {
    const a = prior[metric];
    const b = current[metric];
    return a === undefined || b === undefined ? undefined : b - a;
}
